import { compareDateOnly, type DateOnly } from '../../domain/date-only.ts';
import type { DependencyType, Predecessor, ScheduleItem } from '../../domain/entities/schedule.ts';
import {
  addWorkingDays,
  endDateFor,
  nextWorkingDay,
  type WorkCalendar,
} from '../../domain/work-calendar.ts';

/**
 * A dependency the schedule cannot satisfy because it loops.
 *
 * Carries the loop itself: "Framing → Drywall → Framing" is actionable,
 * where "a cycle was detected" is not.
 */
export class DependencyCycleError extends Error {
  readonly cycle: readonly string[];

  constructor(cycle: readonly string[], titles: readonly string[]) {
    super(`These items depend on each other in a loop: ${titles.join(' → ')}`);
    this.name = 'DependencyCycleError';
    this.cycle = cycle;
  }
}

export class UnknownPredecessorError extends Error {
  readonly itemId: string;
  readonly predecessorId: string;

  constructor(itemId: string, predecessorId: string) {
    super(`Item ${itemId} depends on ${predecessorId}, which is not on this schedule`);
    this.name = 'UnknownPredecessorError';
    this.itemId = itemId;
    this.predecessorId = predecessorId;
  }
}

/** What changed when the schedule was recomputed. */
export interface ScheduleShift {
  readonly itemId: string;
  readonly fromStart: DateOnly;
  readonly toStart: DateOnly;
  readonly fromEnd: DateOnly;
  readonly toEnd: DateOnly;
}

export interface CascadeResult {
  readonly items: readonly ScheduleItem[];
  readonly shifts: readonly ScheduleShift[];
}

/**
 * The earliest `startDate` an item may take to satisfy one predecessor.
 *
 * The four types and the sign of the lag are the whole of the scheduling
 * contract, so they are spelled out here rather than inferred at each call
 * site:
 *
 * - **FS** (finish to start) — the successor starts after the predecessor
 *   finishes. Lag 0 means the next working day, not the same day: "start
 *   when framing is done" does not mean "start the afternoon it ends". A lag
 *   of -1 pulls it back onto the predecessor's last day, which is how an
 *   overlap is expressed.
 * - **SS** (start to start) — lag 0 means they start together.
 * - **FF** (finish to finish) — lag 0 means they finish together.
 * - **SF** (start to finish) — the successor finishes once the predecessor
 *   starts. Rare, and only meaningful with a lag.
 *
 * FF and SF constrain the successor's *end*, so the required start is counted
 * back from that end over the calendar, which is why the duration is needed.
 */
export function earliestStartFor(
  calendar: WorkCalendar,
  type: DependencyType,
  lagDays: number,
  predecessor: Pick<ScheduleItem, 'startDate' | 'endDate'>,
  workDays: number,
): DateOnly {
  switch (type) {
    case 'FS':
      return addWorkingDays(calendar, predecessor.endDate, 1 + lagDays);
    case 'SS':
      return addWorkingDays(calendar, predecessor.startDate, lagDays);
    case 'FF': {
      const requiredEnd = addWorkingDays(calendar, predecessor.endDate, lagDays);
      return startForEnd(calendar, requiredEnd, workDays);
    }
    default: {
      const requiredEnd = addWorkingDays(calendar, predecessor.startDate, lagDays);
      return startForEnd(calendar, requiredEnd, workDays);
    }
  }
}

/** The start a span of `workDays` needs in order to end on `end`. */
function startForEnd(calendar: WorkCalendar, end: DateOnly, workDays: number): DateOnly {
  return workDays <= 1
    ? nextWorkingDay(calendar, end)
    : addWorkingDays(calendar, end, -(workDays - 1));
}

interface Node {
  readonly item: ScheduleItem;
  readonly predecessors: readonly Predecessor[];
}

/**
 * Order the items so every predecessor comes before its successors.
 *
 * Kahn's algorithm, so a cycle shows up as the leftovers rather than as a
 * stack overflow part-way through writing dates.
 */
function topologicalOrder(nodes: readonly Node[]): ScheduleItem[] {
  const byId = new Map(nodes.map((node) => [node.item.id, node]));
  const remaining = new Map<string, number>();
  const successors = new Map<string, string[]>();

  for (const node of nodes) {
    remaining.set(node.item.id, node.predecessors.length);
    for (const predecessor of node.predecessors) {
      if (!byId.has(predecessor.itemId)) {
        throw new UnknownPredecessorError(node.item.id, predecessor.itemId);
      }
      const list = successors.get(predecessor.itemId);
      if (list) list.push(node.item.id);
      else successors.set(predecessor.itemId, [node.item.id]);
    }
  }

  // Stable: ready items are taken in start-date order, so recomputing an
  // unchanged schedule produces an identical result.
  const ready = nodes
    .filter((node) => node.predecessors.length === 0)
    .map((node) => node.item.id)
    .sort((a, b) => compareIds(byId, a, b));

  const ordered: ScheduleItem[] = [];
  while (ready.length > 0) {
    const id = ready.shift()!;
    ordered.push(byId.get(id)!.item);
    for (const successorId of successors.get(id) ?? []) {
      const left = remaining.get(successorId)! - 1;
      remaining.set(successorId, left);
      if (left === 0) {
        ready.push(successorId);
        ready.sort((a, b) => compareIds(byId, a, b));
      }
    }
  }

  if (ordered.length !== nodes.length) {
    // Every node was counted above, so the lookup always finds one.
    const stuck = nodes.filter((node) => remaining.get(node.item.id)! > 0);
    const cycle = traceCycle(stuck, byId);
    throw new DependencyCycleError(
      cycle,
      cycle.map((id) => byId.get(id)!.item.title),
    );
  }

  return ordered;
}

function compareIds(byId: Map<string, Node>, a: string, b: string): number {
  const first = byId.get(a)!.item;
  const second = byId.get(b)!.item;
  return (
    compareDateOnly(first.startDate, second.startDate) ||
    first.title.localeCompare(second.title) ||
    first.id.localeCompare(second.id)
  );
}

/**
 * Walk the leftovers until a node repeats; that repeat closes the loop.
 *
 * Every leftover node has at least one leftover predecessor — that is exactly
 * what kept its unresolved count above zero — so the walk cannot run out of
 * nodes, and both assertions below hold by construction.
 */
function traceCycle(stuck: readonly Node[], byId: Map<string, Node>): string[] {
  const unresolved = new Set(stuck.map((node) => node.item.id));
  const path: string[] = [];
  const seen = new Set<string>();

  let current = stuck[0]!.item.id;
  while (!seen.has(current)) {
    seen.add(current);
    path.push(current);
    current = byId
      .get(current)!
      .predecessors.find((predecessor) => unresolved.has(predecessor.itemId))!.itemId;
  }

  // Close the loop so the message reads as a cycle rather than a list.
  path.push(current);
  return path;
}

/** Throws if the predecessors form a loop, or name an item that is not here. */
export function assertAcyclic(items: readonly ScheduleItem[]): void {
  topologicalOrder(items.map((item) => ({ item, predecessors: item.predecessors })));
}

/**
 * Push every item forward until its predecessors are satisfied.
 *
 * Successors only ever move **later**. The spec is that a successor moves the
 * minimum needed to satisfy its constraint, and pulling one earlier because a
 * predecessor finished sooner would silently rewrite dates a crew has already
 * been given. Bringing work forward stays a deliberate act.
 */
export function cascadeSchedule(
  calendar: WorkCalendar,
  items: readonly ScheduleItem[],
): CascadeResult {
  const ordered = topologicalOrder(
    items.map((item) => ({ item, predecessors: item.predecessors })),
  );
  const current = new Map(items.map((item) => [item.id, item]));
  const shifts: ScheduleShift[] = [];

  for (const item of ordered) {
    if (item.predecessors.length === 0) continue;

    let earliest = item.startDate;
    for (const predecessor of item.predecessors) {
      const resolved = current.get(predecessor.itemId)!;
      const required = earliestStartFor(
        calendar,
        predecessor.type,
        predecessor.lagDays,
        resolved,
        item.workDays,
      );
      if (compareDateOnly(required, earliest) > 0) earliest = required;
    }

    const existing = current.get(item.id)!;
    if (compareDateOnly(earliest, existing.startDate) <= 0) continue;

    const startDate = nextWorkingDay(calendar, earliest);
    const endDate = endDateFor(calendar, startDate, existing.workDays);
    current.set(item.id, { ...existing, startDate, endDate });
    shifts.push({
      itemId: item.id,
      fromStart: existing.startDate,
      toStart: startDate,
      fromEnd: existing.endDate,
      toEnd: endDate,
    });
  }

  return { items: items.map((item) => current.get(item.id)!), shifts };
}

/**
 * Whether an item's own dates already satisfy its predecessors.
 *
 * The editor needs this to tell "this move is fine" from "this move breaks a
 * link", which is the prompt the spec describes when someone drags a
 * successor earlier than its constraint allows.
 */
export function violatedPredecessors(
  calendar: WorkCalendar,
  item: ScheduleItem,
  items: readonly ScheduleItem[],
): Predecessor[] {
  const byId = new Map(items.map((candidate) => [candidate.id, candidate]));
  return item.predecessors.filter((predecessor) => {
    const resolved = byId.get(predecessor.itemId);
    if (!resolved) throw new UnknownPredecessorError(item.id, predecessor.itemId);
    const required = earliestStartFor(
      calendar,
      predecessor.type,
      predecessor.lagDays,
      resolved,
      item.workDays,
    );
    return compareDateOnly(item.startDate, required) < 0;
  });
}
